"use client";

import { useEffect, useState, useRef } from "react";
import { SVG_CHEVRON_DOWN, SVG_CHEVRON_UP } from "../../../Assets/SVG/svg_util";

const DropDownGrouped = ({
  label = "",
  name,
  value,
  options,
  placeholder,
  style,
  onValueChange,
  disabled = false,
  setter,
  baggage = false,
  searchAble = true,
  multiple = false,
}) => {
  const [selectedValues, setSelectedValues] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState("");
  const [lastKeyPressTime, setLastKeyPressTime] = useState(0);
  const dropdownRef = useRef(null);
  const optionsRef = useRef(null);

  const handleClick = () => {
    if (!disabled) setShowDropdown(!showDropdown);
  };

  // Group options and sort by disabled status within each group
  const groupedOptions = options.reduce((acc, option) => {
    const group = option.group?.toString() || "default";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(option);
    // Sort each group's array so disabled options are at the end
    acc[group].sort((a, b) => {
      if (a["option-disabled"] === b["option-disabled"]) return 0;
      return a["option-disabled"] ? 1 : -1;
    });
    return acc;
  }, {});

  useEffect(() => {
    // Initialize selected values from prop
    if (value) {
      const initialValues = Array.isArray(value) ? value : [value];
      setSelectedValues(
        initialValues
          .map((v) => (baggage ? v : options.find((op) => op.value === v)))
          .filter(Boolean)
      );
    } else {
      setSelectedValues([]);
    }
  }, [value, options, baggage]);

  const handleClickOutside = useRef((e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setShowDropdown(false);
    }
  });

  useEffect(() => {
    const handler = handleClickOutside.current;
    if (showDropdown) {
      document.addEventListener("mousedown", handler);
    } else {
      document.removeEventListener("mousedown", handler);
    }
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [showDropdown]);

  const handleSelect = (option) => {
    if (disabled || option["option-disabled"]) return;

    setSelectedValues((prev) => {
      let newValues = [...prev];

      // If option is already selected, remove it
      const existingIndex = newValues.findIndex(
        (v) => v.value === option.value
      );
      if (existingIndex !== -1) {
        newValues.splice(existingIndex, 1);
      } else {
        // If multiple is false, remove any existing selection from the same group
        if (!multiple && option.group !== undefined) {
          newValues = newValues.filter((v) => v.group !== option.group);
        }
        // Add the new selection
        newValues.push(option);
      }

      // Update parent component
      const outputValues = baggage ? newValues : newValues.map((v) => v.value);
      setter?.(outputValues);

      return newValues;
    });

    setError("");
  };

  const handleSelectAll = (group) => {
    setSelectedValues((prev) => {
      const groupOptions = groupedOptions[group].filter(
        (option) => !option["option-disabled"]
      );
      const allSelected = groupOptions.every((option) =>
        prev.some((v) => v.value === option.value)
      );

      let newValues;
      if (allSelected) {
        // Deselect all in the group
        newValues = prev.filter((v) => v.group !== group);
      } else {
        // Select all non-disabled options in the group
        const valuesToAdd = groupOptions.filter(
          (option) => !prev.some((v) => v.value === option.value)
        );
        newValues = [...prev, ...valuesToAdd];
      }

      // Update parent component
      const outputValues = baggage ? newValues : newValues.map((v) => v.value);
      setter?.(outputValues);

      return newValues;
    });

    setError("");
  };

  const getDisplayValue = () => {
    if (selectedValues.length === 0) return "";
    return selectedValues.map((v) => v.name).join(", ");
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) {
      setShowDropdown(true);
      return;
    }

    if (!searchAble) return;

    const currentTime = new Date().getTime();
    const timeSinceLastKeyPress = currentTime - lastKeyPressTime;
    const flatOptions = options.filter((opt) => !opt["option-disabled"]);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < flatOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < flatOptions.length) {
          handleSelect(flatOptions[focusedIndex]);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        break;
      default:
        if (e.key.length === 1) {
          if (timeSinceLastKeyPress > 500) {
            setSearchTerm(e.key.toLowerCase());
          } else {
            setSearchTerm((prev) => prev + e.key.toLowerCase());
          }

          const matchingIndex = flatOptions.findIndex((option) =>
            option.name
              .toLowerCase()
              .startsWith(searchTerm + e.key.toLowerCase())
          );

          if (matchingIndex !== -1) {
            setFocusedIndex(matchingIndex);
          }
        }
    }

    setLastKeyPressTime(currentTime);
  };

  return (
    <div className="">
      {label?.length > 0 && (
        <div className="py-1">
          <label className="text-label">{label}</label>
        </div>
      )}
      <div
        className="relative flex items-center w-full mx-auto"
        ref={dropdownRef}
      >
        <div
          className={`truncate min-h-[40px] w-full py-2 px-2 pr-6 rounded-md outline-none border-2 
            ${showDropdown ? "border-primary" : "border-input"} 
            bg-background text-sm cursor-pointer
            ${disabled ? "bg-muted cursor-not-allowed" : ""}`}
          style={{ ...style }}
          onClick={() => !disabled && setShowDropdown(!showDropdown)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="combobox"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          aria-controls="dropdown-list"
        >
          {getDisplayValue() || placeholder}
        </div>

        {showDropdown && !disabled && (
          <div
            id="dropdown-list"
            ref={optionsRef}
            className="z-10 absolute top-[calc(100%+4px)] left-0 w-full bg-white border
              border-gray-200 rounded-md shadow-md max-h-[50vh] overflow-auto"
            style={{ width: style?.width }}
            role="listbox"
          >
            {Object.entries(groupedOptions).map(([group, groupOptions]) => (
              <div key={group} className="py-1">
                <div className="px-3 py-1.5 text-sm font-semibold bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                  <span>{group === "default" ? "Options" : `${group}`}</span>
                  {multiple && (
                    <button
                      onClick={() => handleSelectAll(group)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {groupOptions.every(
                        (option) =>
                          selectedValues.some(
                            (v) => v.value === option.value
                          ) || option["option-disabled"]
                      )
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  )}
                </div>
                {groupOptions.map((option, index) => {
                  const isSelected = selectedValues.some(
                    (v) => v.value === option.value
                  );
                  return (
                    <div
                      key={option.value}
                      className={`px-3 py-2 cursor-pointer flex items-center gap-3 hover:bg-gray-50
                        ${
                          option["option-disabled"]
                            ? "opacity-50 line-through"
                            : ""
                        }
                        ${isSelected ? "bg-blue-50" : ""}
                        ${focusedIndex === index ? "bg-gray-50" : ""}`}
                      onClick={() =>
                        !option["option-disabled"] && handleSelect(option)
                      }
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option["option-disabled"]}
                    >
                     
                      <div
                        className={`w-4 h-4 border-2 flex items-center justify-center
                        ${isSelected ? "border-blue-500" : "border-gray-300"}`}
                      >
                        {isSelected && <div className="w-2 h-2 bg-blue-500" />}
                        
                      </div>
                      <div className="flex-1  text-start">{option.name}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <span
          className="absolute right-3 transition-transform duration-700 cursor-pointer"
          style={{ color: style?.color || "black" }}
          onClick={handleClick}
        >
          {showDropdown ? <SVG_CHEVRON_UP /> : <SVG_CHEVRON_DOWN />}
        </span>
      </div>
      {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
    </div>
  );
};

export default DropDownGrouped;
